import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Users, Search, Filter, Eye, Plus, Building2 } from 'lucide-react';
import { CreateCareTeamModal } from './CreateCareTeamModal';
import { useCareTeams } from '../../lib/hooks/useCareTeams';
import { LoadingState } from '../common/LoadingState';
import { AlertCard } from '../common/AlertCard';
import { EmptyState } from '../common/EmptyState';

// Type definitions
interface CareTeamManagementProps {
  hospitals: Array<{ id: number; name: string }>;
  doctors?: Array<{ id: number; name: string; specialization?: string }>;
  therapists?: Array<{ id: number; name: string; specialization?: string }>;
  nurses?: Array<{ id: number; name: string; specialization?: string }>;
  physicianAssistants?: Array<{ id: number; name: string; specialization?: string }>;
  onViewTeamPatients?: (teamId: number) => void;
}

export function CareTeamManagement({
  hospitals,
  doctors = [],
  therapists = [],
  nurses = [],
  physicianAssistants = [],
  onViewTeamPatients,
}: CareTeamManagementProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [hospitalFilter, setHospitalFilter] = useState<number | 'all'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Fetch care teams from API
  const { data: careTeamsData, isLoading, error, refetch } = useCareTeams({
    hospital_id: hospitalFilter === 'all' ? undefined : hospitalFilter,
    search: searchQuery || undefined,
    is_active: true,
  });

  const careTeams = careTeamsData?.care_teams || [];

  console.log(careTeams)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-primary text-xl sm:text-2xl">Care Team Management</h2>
          <p className="text-muted-foreground text-sm">Manage and monitor all care teams</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-[#F07D13] hover:bg-[#F07D13]/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Care Team
        </Button>
      </div>

      {/* Search and Filter */}
      <Card className="shadow-lg">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search care teams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select 
              value={hospitalFilter.toString()} 
              onValueChange={(val) => setHospitalFilter(val === 'all' ? 'all' : parseInt(val))}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by hospital" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Hospitals</SelectItem>
                {hospitals.map(hospital => (
                  <SelectItem key={hospital.id} value={hospital.id.toString()}>
                    {hospital.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {isLoading && <LoadingState message="Loading care teams..." />}

      {/* Error State */}
      {error && (
        <AlertCard
          type="error"
          title="Failed to load care teams"
          description={error?.message || "An error occurred while loading care teams"}
          action={
            <Button onClick={() => refetch()} variant="outline">
              Try Again
            </Button>
          }
        />
      )}

      {/* Care Teams Grid */}
      {!isLoading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {careTeams.length === 0 ? (
            <div className="col-span-full">
              <EmptyState
                icon={Users}
                title="No care teams found"
                description="Create a new care team to get started."
              />
            </div>
          ) : (
            careTeams.map(team => (
              <Card key={team.id} className="shadow-lg bg-[#D4F0F8] border-none hover:shadow-xl transition-all">
                <CardHeader className="p-4 pb-3">
                  <CardTitle className="text-primary text-base flex items-start justify-between gap-2">
                    <span className="flex-1 line-clamp-2">{team.name}</span>
                    <Badge
                      className={
                        team.is_active
                          ? 'bg-[#14CAF4] text-white'
                          : 'bg-[#494949] text-white'
                      }
                    >
                      {team.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-3">
                  {/* Hospital */}
                  {team.hospital_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 className="w-4 h-4 text-[#2762AC]" />
                      <span className="text-muted-foreground truncate">{team.hospital_name}</span>
                    </div>
                  )}

                  {/* Description */}
                  {team.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">
                      {team.description}
                    </p>
                  )}

                  {/* Team Members */}
                  <div className="space-y-2">
                    {/* Doctors */}
                    {team.doctors && team.doctors.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">
                          Team :
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {team.doctors.map(doctor => {
                            const initials = doctor.name.split(' ').map(n => n[0]).join('');
                            return (
                              <div
                                key={doctor.id}
                                className="w-8 h-8 rounded-full bg-[#2762AC] flex items-center justify-center text-white text-xs relative group cursor-pointer"
                                title={`${doctor.name}${doctor.specialization ? ` - ${doctor.specialization}` : ''}`}
                              >
                                {initials}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#494949] text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                  {doctor.name}
                                  {doctor.specialization && (
                                    <div className="text-[10px] text-gray-300">{doctor.specialization}</div>
                                  )}
                                  <div className="text-[10px] text-gray-300">Role : Doctor</div>
                                </div>
                              </div>
                            );
                          })}
                          
                          {/* Physician Assistants */}
                          {team.physician_assistants.map(doctor => {
                            const initials = doctor.name.split(' ').map(n => n[0]).join('');
                            return (
                              <div
                                key={doctor.id}
                                className="w-8 h-8 rounded-full bg-[#2762AC] flex items-center justify-center text-white text-xs relative group cursor-pointer"
                                title={`${doctor.name}${doctor.specialization ? ` - ${doctor.specialization}` : ''}`}
                              >
                                {initials}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#494949] text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                  {doctor.name}
                                  {doctor.specialization && (
                                    <div className="text-[10px] text-gray-300">{doctor.specialization}</div>
                                  )}
                                  <div className="text-[10px] text-gray-300">Role : Physician Assistant</div>
                                </div>
                              </div>
                            );
                          })}

                          {/* Therapist */}
                          {team.therapist && (
                            <div className="space-y-1">                        
                              <div className="flex flex-wrap gap-1">
                                    <div
                                      key={2}
                                      className="w-8 h-8 rounded-full bg-[#F07D13] flex items-center justify-center text-white text-xs relative group cursor-pointer"
                                      title={`${team.therapist.name}${team.therapist.specialization ? ` - ${team.therapist.specialization}` : ''}`}
                                    >
                                      {team.therapist.name.split(' ').map(n => n[0]).join('')}
                                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#494949] text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                        {team.therapist.name}
                                        {team.therapist.specialization && (
                                          <div className="text-[10px] text-gray-300">{team.therapist.specialization}</div>
                                        )}
                                        <div className="text-[10px] text-gray-300">Role : Therapist</div>
                                      </div>
                                    </div>
                              </div>
                            </div>
                          )}

                          {/* Nurse */}
                          {team.nurse && (
                            <div className="space-y-1">                        
                              <div className="flex flex-wrap gap-1">
                                    <div
                                      key={2}
                                      className="w-8 h-8 rounded-full bg-[#F07D13] flex items-center justify-center text-white text-xs relative group cursor-pointer"
                                      title={`${team.nurse.name}${team.nurse.specialization ? ` - ${team.nurse.specialization}` : ''}`}
                                    >
                                      {team.nurse.name.split(' ').map(n => n[0]).join('')}
                                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#494949] text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                        {team.nurse.name}
                                        {team.nurse.specialization && (
                                          <div className="text-[10px] text-gray-300">{team.nurse.specialization}</div>
                                        )}
                                        <div className="text-[10px] text-gray-300">Role : Nurse</div>
                                      </div>
                                    </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Patients Count */}
                  <div className="flex items-center justify-between pt-3 border-t border-[#2762AC]/20">
                    <Users className="w-4 h-4 text-[#2762AC]" /> {team.patients.length}
                    
                    <div className="flex items-center  text-sm" style={{flexDirection:"column", alignItems:"flex-end"}}>
                      {team.patients.map((p) => (
                        <div key={p.id}>
                          <b>{p.name}</b> ({p.age} Yrs), &nbsp; 
                          <span className="text-muted-foreground">
                            {p.umr_no}
                          </span>
                        </div>
                      ))}

                    </div>
                    {/*                     
                    <Button
                      size="sm"
                      onClick={() => onViewTeamPatients?.(team.id)}
                      className="bg-[#2762AC] hover:bg-[#2762AC]/90 h-8 px-3"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View
                    </Button> 
                    */}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Create Care Team Modal */}
      <CreateCareTeamModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        hospitals={hospitals}
        doctors={doctors}
        therapists={therapists}
        nurses={nurses}
        physicianAssistants={physicianAssistants}
      />
    </div>
  );
}